/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IAddressAddItem, IAddressAddUpdateProps } from '@msdyn365-commerce-modules/address';
import { IBusinessAccountAddressViewProps } from './sysco-business-account-address';
import {
    ISyscoBusinessAccountAddressConfig,
    ISyscoBusinessAccountAddressResources
} from './sysco-business-account-address.props.autogenerated';

const errorCircleSvg = (
    <svg xmlns='http://www.w3.org/2000/svg' width='20px' height='20px' viewBox='0 0 21 21'>
        <g fill='none' fill-rule='evenodd' stroke='#dc3232' stroke-linecap='round' stroke-linejoin='round' transform='translate(2 2)'>
            <circle cx='8.5' cy='8.5' r='8' />
            <g transform='matrix(0 1 -1 0 17 0)'>
                <path d='m5.5 11.5 6-6' />

                <path d='m5.5 5.5 6 6' />
            </g>
        </g>
    </svg>
);

const mobileNumberRegex = /^(?:\d{10}|\d{3}-\d{3}-\d{4}|\(\d{3}\)\d{3}-\d{4}|\(\d{3}\) \d{3}-\d{4})$/;

/**
 * Address Add Update Component.
 * @param param0 - Root param.
 * @param param0.AddressForm - Address form.
 * @param param0.heading - Address Heading.
 * @param param0.items - IAddressAddItem[].
 * @param param0.hasError - Boolean.
 * @param param0.error - IAddressError.
 * @param param0.isShowSaveButton - Boolean.
 * @param param0.saveButton - Save button.
 * @param param0.isShowCancelButton - Boolean.
 * @param param0.cancelButton - Cancel button.
 * @returns - AddressForm Node.
 */
const AddressAddUpdate: React.FC<IAddressAddUpdateProps & {
    inputDirty: Record<string, { isDirty: boolean; value: string | number }>;
    resources: ISyscoBusinessAccountAddressResources;
    config: ISyscoBusinessAccountAddressConfig;
}> = ({
    AddressForm,
    heading,
    items,
    hasError,
    error,
    isShowSaveButton,
    saveButton,
    isShowCancelButton,
    cancelButton,
    inputDirty,
    resources,
    config
}) => {
    const errorComp = (itemKey: string): React.ReactNode => {
        if (!inputDirty?.[itemKey]?.isDirty) {
            return null;
        }
        const { value } = inputDirty[itemKey];
        const { fieldRequiredError, mobileNoValidationError } = resources;
        const { mobileNoValidationErrorConfig } = config;
        if (!value) {
            return (
                <>
                    {errorCircleSvg}
                    <span>{fieldRequiredError}</span>
                </>
            );
        }
        if (itemKey === 'Phone' && !mobileNumberRegex.test(String(value))) {
            return (
                <>
                    {errorCircleSvg}
                    <span>{mobileNoValidationErrorConfig ?? mobileNoValidationError}</span>
                </>
            );
        }
        return null;
    };

    return (
        <Node {...AddressForm}>
            {heading}
            {items.map((item: IAddressAddItem) => {
                const { AddressItem, key, label, alert, input } = item;
                if (item.key?.toLocaleLowerCase() === 'county') {
                    return null;
                } else {
                    return (
                        <Node {...AddressItem} key={key}>
                            {label}
                            {hasError && alert}
                            {input}
                            {item?.key && inputDirty && inputDirty?.[item.key] && (
                                <div style={{ display: 'flex', marginTop: '5px', color: '#dc3232', gap: '5px' }}>{errorComp(item.key)}</div>
                            )}
                        </Node>
                    );
                }
            })}
            {hasError && (
                <Node {...error.AddressError}>
                    {error.title}
                    {error.message}
                </Node>
            )}
            {isShowSaveButton && saveButton}
            {isShowCancelButton && cancelButton}
        </Node>
    );
};

/**
 * Business Account Address View Component.
 * @param props - Props.
 * @returns - BusinessAccountAddress Module.
 */
const BusinessAccountAddressView: React.FC<IBusinessAccountAddressViewProps &
    ISyscoBusinessAccountAddressResources &
    ISyscoBusinessAccountAddressConfig> = props => {
    const { BusinessAccountAddress, showAddOrUpdateAddress, inputDirty, resources, config } = props;

    return (
        <Module {...BusinessAccountAddress}>
            <AddressAddUpdate {...showAddOrUpdateAddress} inputDirty={inputDirty} resources={resources} config={config} />
        </Module>
    );
};

export default BusinessAccountAddressView;
